import { lazy, Suspense } from "react";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { LoadingState } from "@/components/feedback/states";

const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    );

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  pendingComponent: () => <LoadingState label="Loading…" />,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootLayout() {
  return (
    <>
      <Outlet />
      <Suspense>
        <RouterDevtools />
      </Suspense>
    </>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 4,
        textAlign: "center",
      }}
    >
      {children}
    </Box>
  );
}

function NotFound() {
  return (
    <CenteredMessage>
      <Typography variant="h1">404</Typography>
      <Typography color="text.secondary">That page doesn’t exist.</Typography>
      <Button href={import.meta.env.BASE_URL} variant="contained">
        Back to dashboard
      </Button>
    </CenteredMessage>
  );
}

function RootError({ error }: { error: Error }) {
  return (
    <CenteredMessage>
      <Typography variant="h2">Something broke</Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 440 }}>
        {error.message}
      </Typography>
      <Button onClick={() => window.location.reload()} variant="contained">
        Reload
      </Button>
    </CenteredMessage>
  );
}
